<script setup>
import { Link, router } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon.vue'
import Logo from '@/Shared/Logo.vue'
import { usePage } from '@inertiajs/vue3'
import { ref, watch, inject, onMounted, onBeforeUnmount } from 'vue'

// Obtener el estado compartido desde el componente padre
const isCollapsed = inject('isMenuCollapsed', ref(false))

// Obtener el estado del menú móvil desde el componente padre (LayoutApp)
const isMobileMenuOpen = inject('isMobileMenuOpen', ref(false))

// Estado para controlar qué submenús están expandidos
const expandedSubmenus = ref([])

// Variable para rastrear si estamos desplegando un submenú
const isTogglingSubmenu = ref(false)

// Función para cerrar el menú móvil después de navegar
function closeMenuAfterNavigation() {
  // Solo cerrar el menú si no estamos desplegando un submenú
  if (isMobileMenuOpen.value && !isTogglingSubmenu.value) {
    isMobileMenuOpen.value = false
    document.body.classList.remove('overflow-hidden', 'md:overflow-auto')
  }

  // Resetear el estado de despliegue de submenú
  isTogglingSubmenu.value = false
}

// Configurar event listener para navegación
onMounted(() => {
  router.on('navigate', closeMenuAfterNavigation)
})

// Limpiar event listener
onBeforeUnmount(() => {
  router.off('navigate', closeMenuAfterNavigation)
})

// Función para cerrar el menú móvil (solo para enlaces de navegación, no para toggles de submenú)
function closeMenu() {
  if (isMobileMenuOpen.value) {
    isMobileMenuOpen.value = false
    document.body.classList.remove('overflow-hidden', 'md:overflow-auto')
  }
}

// Función para alternar el estado de un submenú
function toggleSubmenu(id, event) {
  if (isCollapsed.value) return

  // Detener la propagación del evento para evitar que se cierre el menú
  if (event) {
    event.stopPropagation()
  }

  // Marcar que estamos desplegando un submenú
  isTogglingSubmenu.value = true

  // Cancelar cualquier cierre programado del menú
  clearTimeout(window.submenuTimeout)

  // Programar un reset del estado después de un breve retraso
  window.submenuTimeout = setTimeout(() => {
    isTogglingSubmenu.value = false
  }, 300)

  if (expandedSubmenus.value.includes(id)) {
    expandedSubmenus.value = expandedSubmenus.value.filter((item) => item !== id)
  } else {
    expandedSubmenus.value.push(id)
  }
}

// Función para verificar si un submenú está expandido
function isSubmenuExpanded(id) {
  return expandedSubmenus.value.includes(id)
}

// Estructura del menú con submenús
const menuItems = [
  {
    id: 'dashboard',
    url: '/dashboard',
    includedComponents: ['Dashboard'],
    text: 'Agenda',
    icon: 'RocketLaunch',
    type: 'link'
  },
  {
    id: 'classes',
    text: 'Clases',
    icon: 'SquareStack',
    type: 'submenu',
    children: [
      {
        id: 'lessons',
        url: '/lessons',
        includedComponents: ['Lessons/Index', 'Lessons/Create', 'Lessons/Edit'],
        text: 'Horarios',
        icon: 'Calendar'
      },
      {
        id: 'coaches',
        url: '/coaches',
        includedComponents: ['Coaches/*'],
        text: 'Entrenadores',
        icon: 'AcademicCap'
      },
      {
        id: 'students',
        url: '/students',
        includedComponents: ['Students/*'],
        text: 'Alumnos',
        icon: 'UserGroup'
      },
      {
        id: 'levels',
        url: '/lessons/levels',
        includedComponents: ['Lessons/Levels/*'],
        text: 'Niveles',
        icon: 'AcademicCap'
      }
    ]
  },
  {
    id: 'players',
    url: '/players',
    includedComponents: ['Players/*'],
    text: 'Jugadores',
    icon: 'GlobeAmericas',
    type: 'link'
  },
  {
    id: 'rankings',
    url: '/rankings',
    includedComponents: ['Rankings/*'],
    text: 'Rankings',
    icon: 'ChartBar',
    type: 'link'
  },
  {
    id: 'settings',
    url: '/settings',
    includedComponents: ['Settings/*'],
    text: 'Configuración',
    icon: 'Cog',
    type: 'link'
  },
  {
    id: 'logout',
    url: '/logout',
    method: 'post',
    includedComponents: [],
    text: 'Salir',
    icon: 'ArrowLeftOnRectangle',
    type: 'link'
  }
]

// Función para verificar si un enlace está activo
function isActive(link) {
  const currentComponent = usePage().component
  if (
    link.includedComponents &&
    link.includedComponents.some((component) => component.includes('*'))
  ) {
    return link.includedComponents.some((component) =>
      currentComponent.startsWith(component.slice(0, -1))
    )
  }
  return link.includedComponents && link.includedComponents.includes(currentComponent)
}

// Función para verificar si un submenú tiene algún elemento activo
function hasActiveChild(submenu) {
  return submenu.children && submenu.children.some((child) => isActive(child))
}

// Auto-expandir submenús que contienen elementos activos
watch(
  () => usePage().component,
  (newComponent) => {
    menuItems.forEach((item) => {
      if (item.type === 'submenu' && !expandedSubmenus.value.includes(item.id)) {
        if (
          item.children.some((child) => {
            if (
              child.includedComponents &&
              child.includedComponents.some((comp) => comp.includes('*'))
            ) {
              return child.includedComponents.some((comp) =>
                newComponent.startsWith(comp.slice(0, -1))
              )
            }
            return child.includedComponents && child.includedComponents.includes(newComponent)
          })
        ) {
          expandedSubmenus.value.push(item.id)
        }
      }
    })
  },
  { immediate: true }
)
</script>

<template>
  <nav class="gl-sidebar">
    <!-- Cabecera del menú solo visible en desktop -->
    <div class="hidden md:flex items-center justify-center py-3 px-4 mb-4">
      <!-- Logo de CourtControl (pequeño a la izquierda) -->
      <Link class="inline-block" href="/dashboard">
        <Logo
          v-show="!isCollapsed"
          :minimal="true"
          :logo-classes="'w-12 h-12'"
          :text-classes="'text-xs font-medium'"
        />
      </Link>
    </div>

    <!-- Elementos del menú -->
    <ul class="list-none p-0 m-0 px-2">
      <!-- Enlaces normales -->
      <li v-for="item in menuItems" :key="item.id" class="mb-1">
        <template v-if="item.type === 'link'">
          <Link
            :href="item.url"
            :method="item.method || 'get'"
            as="div"
            class="block"
            @click="closeMenu"
          >
            <div
              class="flex items-center py-2.5 px-4 text-secondary-100 hover:bg-primary-600/20 rounded-lg transition-all duration-200 cursor-pointer"
              :class="{ 'bg-primary-600 text-white shadow-md': isActive(item) }"
            >
              <div class="mr-3 flex-shrink-0">
                <Icon
                  :name="item.icon"
                  class="w-5 h-5"
                  :class="{ 'text-primary-300': !isActive(item), 'text-white': isActive(item) }"
                />
              </div>
              <span
                class="text-sm font-medium truncate flex-grow cursor-pointer"
                :class="{ 'font-semibold': isActive(item) }"
              >
                {{ item.text }}
              </span>
              <div v-if="isActive(item)" class="ml-2 w-1.5 h-1.5 bg-accent-blue rounded-full"></div>
            </div>
          </Link>
        </template>

        <!-- Submenús -->
        <template v-else-if="item.type === 'submenu'">
          <div>
            <!-- Cabecera del submenú -->
            <div
              class="flex items-center py-2.5 px-4 text-secondary-100 hover:bg-primary-600/20 rounded-lg cursor-pointer transition-all duration-200"
              :class="{ 'bg-primary-600/30': hasActiveChild(item) || isSubmenuExpanded(item.id) }"
              @click="toggleSubmenu(item.id, $event)"
            >
              <div class="mr-3 flex-shrink-0">
                <Icon :name="item.icon" class="w-5 h-5 text-primary-300" />
              </div>
              <span
                class="text-sm font-medium truncate flex-grow cursor-pointer"
                :class="{ 'font-semibold': hasActiveChild(item) }"
              >
                {{ item.text }}
              </span>
              <!-- Icono cuando el submenú está expandido (flecha hacia abajo) -->
              <svg
                v-if="isSubmenuExpanded(item.id)"
                class="w-3.5 h-3.5 transition-transform duration-200"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                  clip-rule="evenodd"
                />
              </svg>
              <!-- Icono cuando el submenú está colapsado (flecha hacia la derecha) -->
              <svg
                v-else
                class="w-3.5 h-3.5 transition-transform duration-200"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>

            <!-- Elementos del submenú -->
            <ul
              v-show="!isCollapsed && isSubmenuExpanded(item.id)"
              class="list-none pl-0 overflow-hidden transition-all duration-200 mt-1 mb-1"
            >
              <li v-for="child in item.children" :key="child.id" class="ml-6 mb-1">
                <Link
                  :href="child.url"
                  :method="child.method || 'get'"
                  as="div"
                  class="block"
                  @click="closeMenu"
                >
                  <div
                    class="flex items-center py-2 px-4 text-secondary-100 hover:bg-primary-600/20 rounded-lg transition-all duration-200"
                    :class="{ 'bg-primary-600 text-white shadow-md': isActive(child) }"
                  >
                    <div class="mr-3 flex-shrink-0">
                      <Icon
                        :name="child.icon"
                        class="w-4 h-4"
                        :class="{
                          'text-primary-300': !isActive(child),
                          'text-white': isActive(child)
                        }"
                      />
                    </div>
                    <span
                      class="text-sm font-medium truncate flex-grow cursor-pointer"
                      :class="{ 'font-semibold': isActive(child) }"
                    >
                      {{ child.text }}
                    </span>
                    <div
                      v-if="isActive(child)"
                      class="ml-2 w-1.5 h-1.5 bg-white rounded-full"
                    ></div>
                  </div>
                </Link>
              </li>
            </ul>
            <!-- Elementos del submenú cuando está colapsado -->
            <div v-if="isCollapsed && hasActiveChild(item)" class="mt-1 mb-1 flex justify-center">
              <div class="w-1.5 h-1.5 bg-primary-400 rounded-full"></div>
            </div>
          </div>
        </template>
      </li>
    </ul>
  </nav>
</template>

<style scoped>
.gl-sidebar {
  padding: 0.75rem 0.5rem;
  height: 100%;
  overflow-y: auto;
}

/* Estilos específicos para el menú móvil */
@media (max-width: 767px) {
  .gl-sidebar {
    padding-top: 0;
  }
}
</style>
